import { Upload } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { FileField, USER_DOCUMENT_MAX_BYTES } from '../../../../components/inputs/FileField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { formatBytes } from '../../../../lib/formatBytes';
import { personnelApi } from '../personnelApi';
import type { PersonnelDocument } from '../personnelTypes';
import type { PersonnelDocumentType } from '../../personnel-documents/personnelDocumentTypes';

const ID = 'personnel-document';

const DESCRIPTION =
  `The scanned document — PDF, PNG or JPEG, up to ${formatBytes(USER_DOCUMENT_MAX_BYTES)}. ` +
  "It is filed into the Repository, and the type is verified against the file's own leading " +
  'bytes rather than its name.';

/**
 * Files one document against one checklist row of a 201 file — or, given `replacing`, swaps
 * one filed document's file.
 *
 * **The checklist row is the modal's subject, not a field.** The panel opens this from the row
 * it belongs to, so the kind is already decided and is stated in the title rather than asked
 * for again — the picker would be a control whose only legal answer is the one the reader has
 * already given.
 *
 * **Upload ADDS; Replace SWAPS one file.** Several documents may name one kind — an employee
 * holds one birth certificate and ten appointments — so filing never overwrites. Replacing is
 * its own act on one document, reached from that document's line, and keeps the row and its
 * kind; the superseded file is soft-removed with its bytes kept.
 *
 * **No Autogenerate, and this is the stated exemption rather than an omission** — the one field
 * is a binary whose format the server verifies by magic bytes, and there is no textual format
 * to write honestly. The prop is omitted so no button renders.
 */
export function UploadDocumentModal({
  personnelId,
  documentType,
  replacing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  documentType: Pick<PersonnelDocumentType, 'id' | 'name'>;
  /** The document whose file is being swapped. Absent, the modal files a new one. */
  replacing?: PersonnelDocument;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const file = files[0] ?? null;
  const tooLarge = file !== null && file.size > USER_DOCUMENT_MAX_BYTES;

  async function submit() {
    if (file === null || tooLarge) return;

    setSaving(true);
    setProgress(0);
    setError(null);

    try {
      if (replacing) {
        await personnelApi.replaceDocument(personnelId, replacing.id, file, setProgress);
      } else {
        await personnelApi.addDocument(personnelId, documentType.id, file, setProgress);
      }

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not file that document.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={replacing ? `Replace ${replacing.fileName}` : `Upload ${documentType.name}`}
      size="md"
      id={`${ID}-modal`}
      confirm={{
        id: `${ID}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Button
            variant="primary"
            id={`${ID}-submit`}
            onClick={() => void submit()}
            loading={saving}
            /*
              Real upload progress, not a flattened word — the request carries bytes and the
              client measures them, so the label says how far it has got.
            */
            loadingLabel={progress === null ? 'Submitting' : `Filing ${progress}%`}
            disabled={file === null || tooLarge || !confirmed}
            leading={<Upload className="h-4 w-4" aria-hidden="true" />}
          >
            Submit
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error.message}
          </p>
        ) : null}

        {/* The CONSEQUENCE, stated on the form where it is read before the tick. */}
        {replacing ? (
          <p className="text-sm text-slate-600">
            Filed under <span className="font-medium text-slate-800">{documentType.name}</span>.
            The file you choose takes the place of{' '}
            <span className="font-medium text-slate-800">{replacing.fileName}</span> on this
            record; the previous file is kept in the Repository, no longer listed here.
          </p>
        ) : (
          <p className="text-sm text-slate-600">
            Filed under <span className="font-medium text-slate-800">{documentType.name}</span>.
            This adds a document — anything already filed under it stays.
          </p>
        )}

        <FileField
          id={`${ID}-file`}
          label="Document"
          description={DESCRIPTION}
          files={files}
          onChange={setFiles}
          required
          disabled={saving}
          error={error?.fieldError('file') ?? undefined}
        />
      </div>
    </Modal>
  );
}
